import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Col, FormControl, Row} from 'react-bootstrap';
import {Conditional} from "../../Conditional";
import {ConfirmationDialog} from "../../Common/ConfirmationDialog";
import {Glyphicon} from "../../Common/GlyphIcon";

import {ObjectUtils} from "../../../Utils";

import {service as SurveyAnswerService} from "../../../Services/SurveyAnswerService";
import {service as SurveyService} from "../../../Services/SurveyService";
import {service as SurveySubmissionService} from "../../../Services/SurveySubmissionService";
import {service as SurveyTypeService} from "../../../Services/SurveyTypeService";


import {CopyAnswersModal} from "./CopyAnswersModal";

import {InfoIcon} from "../../Common/InfoIcon";
import {SurveyForm} from "../../Common/SurveyForm/SurveyForm";
import {useMakePageRequest} from "../../../Hooks/RequestHooks";
import {service as ConservationActionTypeService} from "../../../Services/ConservationActionTypeService";
import {useChangeTool} from "../../../Hooks/RouteHooks";
import {useDefaultErrorHandler, useSurveyAnswerConflictHandler} from "../../../Hooks/ErrorHooks";
import {useEditContextNodeId, usePermission} from "../../../Hooks/PermissionHooks";
import {usePrimeCache} from "../../../Hooks/ServiceHooks";
import {RiskAssessments} from "./RiskAssessments";
import {PlantQuantities} from "./PlantQuantities";
import {ModeledUpliftSurveySection} from "./ModeledUpliftSurveySection";
import {CustomSubmissionBoundary} from "./CustomSubmissionBoundary";
import {
	allSurveyQuestions,
	useFriendlyAnswers,
	useOnlyWhen
} from "../../../../../monitoring/src/Hooks/SharedSurveyHooks";
import {ModeledUpliftReadOnlySurveySection} from "./ModeledUpliftReadOnlySurveySection";
import {useIsMounted} from "../../../Hooks/useIsMounted";
import {SurveySubmissionWidget} from "./SurveySubmissionWidget";
import {useIsSectionComplete} from "../../../Hooks/SurveyFormNavigationHooks";
import {useIsSectionReadOnly} from "../../../Hooks/SubmissionBoundaryHooks";
import {answerValuesDiffer} from "../../../Utils/surveySubmissionUtils";

export const SurveyComponent = (props) => {
	
	// const context = useSelector(state => state.contextState.context);
	const { 
		ConservationAction: conservationAction,
		OnConservationActionChange,
		SurveyTypeId: surveyTypeId,
		SurveyFormProps: surveyFormProps,
		reloadData: reloadConservationAction = () => {},
		CustomSections: customSections,
	} = props;
	
	usePrimeCache([ConservationActionTypeService, SurveyTypeService]);
	
	const conservationActionId = conservationAction?.conservationActionId;
	
	const makePageRequest = useMakePageRequest();
	const handleSurveyAnswerConflict = useSurveyAnswerConflictHandler();
	const changeTool = useChangeTool();
	const defaultErrorHandler = useDefaultErrorHandler();
	const hasPermissions = usePermission();
	const canEditContextNodeId = useEditContextNodeId();
	const canReviewSubmissions = hasPermissions('review_survey_submissions');
	const isMounted = useIsMounted();
	
	const [isLoaded, setIsLoaded] = useState(false);
	const [survey, setSurvey] = useState(null);
	const [surveyAnswers, setSurveyAnswers] = useState([]);
	// The answers exactly as the server last returned them. surveyAnswers drifts
	// from this the moment an input writes on load (the contact input selects a
	// project contact before anyone has edited), so the submission widget reads
	// this set, not surveyAnswers, for what a submit would actually find.
	const [savedSurveyAnswers, setSavedSurveyAnswers] = useState([]);
	const [surveyPlantQuantities, setSurveyPlantQuantities] = useState([]);
	const [surveyRiskAssessments, setSurveyRiskAssessments] = useState([]);
	const [showCopyAnswersModal, setShowCopyAnswersModal] = useState(false);
	const [saveDialog, setSaveDialog] = useState(false);
	const [validationObject, setValidationObject] = useState({});
	const [editMode, setEditMode] = useState(false);
	const [currentSubmission, setCurrentSubmission] = useState(undefined); // undefined = not yet fetched
	const [submissionRefreshKey, setSubmissionRefreshKey] = useState(0);
	const [showReopenDialog, setShowReopenDialog] = useState(false);
	const [reopenNotes, setReopenNotes] = useState('');

	// The active surveyAnswerIds the server returned when this form last
	// fetched answers (initial load and the reload after every save). Round-tripped
	// with the save so the server can reject writes over answers that changed
	// underneath the open form. Kept in refs — not derived from form state at save
	// time — because deliberate deletes remove rows from surveyAnswers state and
	// would otherwise be indistinguishable from background changes. copyAnswers
	// deliberately does NOT refresh this: the copy is unsaved templates and the
	// server's active set is unchanged.
	const loadedSurveyAnswerIdsRef = useRef(null);
	// codes the user actually touched this edit session (edits, deletes, copied-in
	// answers) — the set re-applied on top of a conflict reload so their work survives
	const dirtyAnswerCodesRef = useRef(new Set());
	
	const surveyType = (SurveyTypeService.CachedValueById(surveyTypeId)?.result);
	const conservationActionType = (ConservationActionTypeService.CachedValueById(conservationAction?.conservationActionTypeId)?.result);
	
	const allQuestions = allSurveyQuestions(survey);

	// Computed with full customSections so the widget's progress counter is accurate
	// even for sections with custom completion logic (RiskAssessments, PlantQuantities, etc.)
	const isSectionComplete = useIsSectionComplete(survey, conservationAction, {
		"customRiskAssessment": RiskAssessments,
		"customPlantQuantity": PlantQuantities,
		"customModeledUplift": ModeledUpliftSurveySection,
		"customModeledUpliftReadOnly": ModeledUpliftReadOnlySurveySection,
		"customSubmissionBoundary": CustomSubmissionBoundary,
	}, {
		PlantQuantities: surveyPlantQuantities,
		RiskAssessments: surveyRiskAssessments,
	});

	const friendlyAnswers = useFriendlyAnswers(surveyAnswers, survey);
	const {getVisibleQuestions} = useOnlyWhen();
	const isSectionReadOnly = useIsSectionReadOnly(survey, canReviewSubmissions);
	
	const fetchSurvey = async (surveyTypeId, conservationActionId) => {
		if (surveyTypeId != null && conservationActionId != null) {
			try {
				const surveysResponse = await SurveyService.GetSurveysByConservationActionId(surveyTypeId, conservationActionId);
				return (surveysResponse?.result && surveysResponse.result.length > 0) ? surveysResponse.result[0] : null;
			} catch (e) {
				console.error("Failed to load survey:", e);
				return null;
			}
		} else {
			return null;
		}
	};
	
	const fetchSurveyAnswersResponse = async (surveyTypeId, conservationActionId) => {
		if (surveyTypeId != null && conservationActionId != null) {
			try {
				const response = await SurveyAnswerService.GetSurveyAnswers(surveyTypeId, conservationActionId);
				// This is the only place the server's active answer set for the
				// current CA enters the page, so it is where the conflict snapshot resets.
				loadedSurveyAnswerIdsRef.current = (response?.result ?? [])
					.map((answer) => answer.surveyAnswerId)
					.filter((id) => id != null);
				dirtyAnswerCodesRef.current = new Set();
				if (isMounted()) {
					setSavedSurveyAnswers(response?.result ?? []);
				}
				return response;
			} catch (e) {
				console.error("Failed to load survey answers:", e);
				return null;
			}
		}
		return null;
	}
	
	// sets survey answers, plant quantities, and risk assessments from the response
	// and optionally sets the survey if provided. This method wraps the multiple
	// setState calls with setIsLoaded to avoid onAnswersChange happening during intermediate states
	const setSurveyAnswersFromResponse = (surveyAnswersResponse, survey = null) => {
		if(isMounted() && surveyAnswersResponse) {
			console.log("SurveyComponent: Setting survey answers from response", surveyAnswersResponse);
			setIsLoaded(false);
			if(survey) { setSurvey(survey); }
			setSurveyAnswers(surveyAnswersResponse.result);
			setSurveyPlantQuantities(surveyAnswersResponse.SurveyAnswerPlantQuantityZone);
			setSurveyRiskAssessments(surveyAnswersResponse.risks);
			setIsLoaded(true);
		}
	};
	
	// used to reload answers after saving
	// or if the user changes the survey type or conservation action
	// while on this page
	const reloadData = async () => {
		const [_, surveyAnswersResponse] = await Promise.all([
			reloadConservationAction(),
			fetchSurveyAnswersResponse(surveyTypeId, conservationActionId),
		]);
		
		setSurveyAnswersFromResponse(surveyAnswersResponse);
		setSubmissionRefreshKey(k => k + 1);
	}
	
	// Recovery for a 409 answers conflict. The save was rolled back because
	// an answer it would have overwritten changed after this form loaded. Refetch the
	// authoritative answers (which also resets the conflict snapshot), then re-apply
	// the codes the user touched this session so their work is not lost — the user
	// reviews the combined state and saves again against the fresh snapshot. Never
	// retry the stale payload as-is: the save is full-replace, so a blind retry would
	// re-clobber the background change.
	const reloadAndMergeAnswers = async () => {
		const pendingAnswers = surveyAnswers;
		const pendingDirtyCodes = new Set(dirtyAnswerCodesRef.current);

		await reloadData();

		if (pendingDirtyCodes.size === 0) {
			return;
		}

		const pendingByCode = new Map(pendingAnswers.map((answer) => [String(answer.code), answer]));

		setSurveyAnswers((reloadedAnswers) => {
			const reloadedByCode = new Map(reloadedAnswers.map((answer) => [String(answer.code), answer]));
			// fresh server answers win everywhere the user didn't touch; touched codes
			// get the user's pending row back (or stay removed, for a pending delete)
			const merged = reloadedAnswers.filter((answer) => !pendingDirtyCodes.has(String(answer.code)));
			for (const code of pendingDirtyCodes) {
				const pending = pendingByCode.get(code);
				if (!pending) {
					continue;
				}
				// re-key the pending row to the reloaded row's identity: the ids it was
				// loaded with are stale, and replaying one for an answer the server no
				// longer has would read as yet another conflict on the retry save
				const reapplied = {...pending};
				const reloadedRow = reloadedByCode.get(code);
				if (reloadedRow?.surveyAnswerId != null) {
					reapplied.surveyAnswerId = reloadedRow.surveyAnswerId;
				} else {
					delete reapplied.surveyAnswerId;
				}
				merged.push(reapplied);
			}
			return merged;
		});

		// the merged edits are still unsaved — keep them re-appliable if the retry
		// save hits a second conflict
		dirtyAnswerCodesRef.current = pendingDirtyCodes;
	};

	const onSurveyChanged = async (surveyTypeId, conservationActionId) => {
		// Clear the current survey immediately so the render guard (survey === null) hides
		// the entire UI while the new survey loads — prevents stale data from a previous
		// survey type flashing briefly before the new one is ready.
		setSurvey(null);
		try {
			const [survey, surveyAnswersResponse] = await Promise.all([
				fetchSurvey(surveyTypeId, conservationActionId),
				fetchSurveyAnswersResponse(surveyTypeId, conservationActionId),
			]);
			
			// if there is no valid survey for this conservation action
			// and survey type then send the user back to the summary page
			if(!survey) {
				changeTool("summary");
				return;
			}
			
			setSurveyAnswersFromResponse(surveyAnswersResponse, survey);
			
		} catch (e) {
			console.error("Failed to reload survey data:", e);
		}
	}
	
	// when the answers change call onAnswerChange
	// to remove answers for questions that are no longer visible
	useEffect(() => {
		// React 17 doesn't batch state updates in all cases
		// so this isLoaded flag ensures that we only call onAnswerChange
		// when we are fully loaded and not in an intermediate state
		// that has the wrong survey answers for the wrong survey questions 
		if(isLoaded) {
			onAnswerChange();
		}
	}, [friendlyAnswers, isLoaded]);
	
	// load the survey and answers when the conservation action or survey type changes
	useEffect(() => {
		void onSurveyChanged(surveyTypeId, conservationActionId);
	}, [surveyTypeId, conservationActionId]);
	
	const copyAnswers = (sourceConservationActionId, sourceSurveyTypeId) => {
		
		setShowCopyAnswersModal(false);
		
		let currentConservationActionId = conservationAction.conservationActionId;
		let currentSurveyId = survey.surveyId;
		
		if (sourceConservationActionId && sourceSurveyTypeId && currentConservationActionId && currentSurveyId) {
			return makePageRequest(
				() => Promise.all([
					SurveyAnswerService.CopySurveyAnswers(sourceConservationActionId, sourceSurveyTypeId, currentConservationActionId, currentSurveyId)
				]),
				([sourceAnswers]) => {
					setEditMode(true);
					setSurveyAnswersFromResponse(sourceAnswers);
					// The copied answers are unsaved user intent (the server's
					// active set is unchanged — the copy endpoint returns id-less
					// templates), so they must survive a conflict reload like any edit
					for (const answer of (sourceAnswers?.result ?? [])) {
						if (answer?.code) {
							dirtyAnswerCodesRef.current.add(String(answer.code));
						}
					}
				}
			);
		}
	}
	
	const canPullPreviousAnswers = () => {
		// only allowed for non-permit survey types
		if (survey?.surveyTypeId < 4) {
			return true;
		}
	}
	
	const onValidation = useCallback((questionId, message) => {
		
		// this never clears the old ones?
		let validation = { ...validationObject };
		
		if (message !== null) {
			validation[questionId] = message;
		} else {
			// this never clears the old ones?
			if (questionId in validation) {
				delete validation[questionId];
			}
		}
		
		setValidationObject(validation);
	}, [JSON.stringify(validationObject)]);
	
	const onAnswerChange = (newAnswer = null) => {

		if (newAnswer && !newAnswer.code) {
			console.warn('onAnswerChange: answer is missing code — it will be silently dropped by the visibility filter. Use buildAnswer() when constructing answers.', newAnswer);
		}

		// Remember which codes were touched this edit session so a conflict
		// reload can re-apply them on top of the fresh server answers. Only a change
		// that actually differs from the answer already in state counts as touched:
		// some inputs re-write their loaded value on init (the contact input fires
		// writeAnswer whenever the contact roster reloads, including on tab focus),
		// and marking those dirty would let a load-derived value win the post-conflict
		// merge and silently re-clobber the background change it just lost to.
		if (newAnswer?.code) {
			const currentAnswer = surveyAnswers.find(
				(answer) => String(answer.code) === String(newAnswer.code)
			);
			if (!currentAnswer || answerValuesDiffer(currentAnswer, newAnswer)) {
				dirtyAnswerCodesRef.current.add(String(newAnswer.code));
			}
		}

		// Compute visible question codes here, outside the functional updater, so we use the
		// current render's friendlyAnswers snapshot consistently across all concurrent updates.
		// SB-3022: key by `code`, not surveyQuestionId — an answer carried over from another
		// subprogram's survey keeps a stale surveyQuestionId, so a qid-based visibility filter would
		// purge it from state on the next change.
		const visibleCodes = getVisibleQuestions(allQuestions, friendlyAnswers)
			.map((question) => question.code);

		// Use a functional updater so that concurrent calls (e.g. multiple contact_relationship
		// questions firing their init effect in the same React render cycle) each receive the
		// already-accumulated prevAnswers rather than the same stale closure value — preventing
		// the "last write wins" problem where only the final setSurveyAnswers call survives.
		setSurveyAnswers(prevAnswers => {
			let newCode = newAnswer?.code ?? null;
			let mergedAnswer = newAnswer;

			let answers = [...prevAnswers]
				.filter(answer => {
					if(newCode && String(answer.code) === String(newCode)) {
						// maintain the primaryKey and other metadata
						// from the previous answer if it existed
						mergedAnswer = {
							...answer,
							...newAnswer,
						};
						return false;
					}
					// remove answers that are no longer visible
					return visibleCodes.includes(answer.code);
				})
				.filter(Boolean);

			if(mergedAnswer) {
				// add the question if it didn't exist?
				answers.push(mergedAnswer);
			}

			return answers;
		});
	}
	
	const deleteAnswer = (answer) => {
		let answers = [...surveyAnswers];
		// SB-3022: remove by `code` so a carried-over answer (stale surveyQuestionId) is matched.
		answers = answers.filter((x) => String(x.code) !== String(answer.code));

		// A delete is touched intent too — a conflict reload must re-apply
		// the removal, not resurrect the answer from the server
		if (answer?.code) {
			dirtyAnswerCodesRef.current.add(String(answer.code));
		}

		setSurveyAnswers(answers);
	}
	
	const onCustomDataChange = (data) => {
		setSurveyPlantQuantities(data.PlantQuantities);
		setSurveyRiskAssessments(data.RiskAssessments);
	}
	
	const save = (displayPopup = true) => {
		
		let answers = ObjectUtils.Clone(surveyAnswers);
		let quantities = ObjectUtils.Clone(surveyPlantQuantities);
		let risks = ObjectUtils.Clone(surveyRiskAssessments);
		
		// the server also filters by this, so this shouldn't
		// be necessary, but there is no reason to send this
		// extra data across the wire
		let conservationActionSampleEventsToUpdate = null;
		let conservationActionPermitsToUpdate = null;
		if (surveyType?.conservationActionPhaseId && conservationAction?.ConservationActionSampleEvents) {
			let conservationActionSampleEvents = ObjectUtils.Clone(conservationAction.ConservationActionSampleEvents)
			conservationActionSampleEventsToUpdate = conservationActionSampleEvents
				.filter((casEvent) => casEvent.conservationActionPhaseId == surveyType.conservationActionPhaseId)
		}
		
		if (surveyType?.code === 'permit' && conservationAction?.ConservationActionPermits) {
			conservationActionPermitsToUpdate = conservationAction.ConservationActionPermits;
		}
		
		return makePageRequest(
			() => SurveyAnswerService.UpdateSurveyAnswers(
				answers,
				quantities,
				risks,
				survey.surveyId,
				conservationAction.conservationActionId,
				conservationActionSampleEventsToUpdate,
				conservationActionPermitsToUpdate,
				loadedSurveyAnswerIdsRef.current
			),
			() => {
				setSaveDialog(displayPopup);
				void reloadData();
			},
			// Two conflict guards can 409 this save; either way the whole request was
			// rolled back. Answers conflict: the survey answers changed under us — reload them
			// and re-apply the user's unsaved edits. Uplift conflict: the modeled uplift
			// changed under us (e.g. a Fulcrum sync) — reload ONLY the conservation
			// action (refreshing the uplift values); the user's unsaved survey answers
			// stay in local state so they can review and save again without losing work.
			(error) => handleSurveyAnswerConflict(
				error,
				() => void reloadAndMergeAnswers(),
				() => void reloadConservationAction()
			)
		);
	}
	
	const saveAndContinue = async () => {
		await save(true);
	}
	
	const saveAndExit = async () => {
		await save(false);
		await exitEditMode();
	}
	
	const discardAndExit = async () => {
		await reloadData();
		await exitEditMode();
	}
	
	const enterEditMode = () => {
		setEditMode(true);
	}

	// Edit-button click handler. Review-complete surveys are locked for everyone
	// (the answers API rejects writes), so instead of entering edit mode directly
	// the reviewer is routed through the "Reopen Survey" dialog, which moves the
	// submission back to in-review before unlocking the form.
	const onEditClick = () => {
		// Still loading the submission (only reviewers see the button in this state —
		// see the anti-flash handling in canEdit). We can't know yet whether the
		// survey is review-complete, so ignore the click until the fetch lands.
		if (currentSubmission === undefined) return;
		if (currentSubmission?.SurveySubmissionStatus?.code === 'review-complete') {
			setShowReopenDialog(true);
			return;
		}
		enterEditMode();
	}

	const closeReopenDialog = () => {
		setShowReopenDialog(false);
		setReopenNotes('');
	}

	const doReopen = async () => {
		try {
			const response = await SurveySubmissionService.Unsubmit({
				conservationActionId,
				surveyTypeId,
				notes: reopenNotes.trim(),
			});
			// Only unlock the form if the server actually reopened the review.
			const updated = response?.result ?? response;
			if (updated?.SurveySubmissionStatus?.code !== 'in-review') {
				throw new Error('The survey could not be reopened.');
			}
			// The widget owns the submission fetch; bump the refresh key so it
			// re-fetches and pushes the in-review submission back up to us.
			setSubmissionRefreshKey(k => k + 1);
			closeReopenDialog();
			enterEditMode();
		} catch (e) {
			// Stay in view mode; the dialog closes so the error modal is visible.
			setShowReopenDialog(false);
			defaultErrorHandler(e);
		}
	}

	const exitEditMode = () => {
		setEditMode(false);
		setValidationObject({});
	}
	
	const canEdit = () => {
		if (conservationAction) {
			if (hasPermissions('update_conservation_action')) {
				if (!canEditContextNodeId(conservationAction.contextNodeId)) return false;
				// Suppliers cannot edit while the survey is locked (in-review or review-complete).
				// Treat undefined (still loading) as locked so the Edit button doesn't flash
				// into view while the widget fetches the submission status.
				// Reviewers (canReviewSubmissions) may always edit regardless of status.
				if (!canReviewSubmissions) {
					const code = currentSubmission?.SurveySubmissionStatus?.code;
					if (currentSubmission === undefined || code === 'in-review' || code === 'review-complete') return false;
				}
				return true;
			}
		}
		return false;
	}
	
	const isValid = Object.keys(validationObject).length === 0;
	
	// don't render anything until we are loaded.
	if (survey === null || surveyAnswers == null || conservationActionType === null || surveyType === null) {
		return null;
	}
	
	return <Row>
		<ConfirmationDialog
			Title="Saved"
			Body="Save Successful!"
			Display={saveDialog}
			ConfirmationButtonMessage="OK"
			OnConfirm={() => {
				setSaveDialog(false);
			}}
		/>
		<ConfirmationDialog
			Title="Reopen Survey"
			Display={showReopenDialog}
			ConfirmationButtonMessage="Reopen"
			CancelButtonMessage="Cancel"
			DisableConfirmationButton={!reopenNotes.trim()}
			OnConfirm={() => void doReopen()}
			OnClose={closeReopenDialog}
		>
			<p>The review of this survey is complete, so it can no longer be edited.
				To make changes, the survey must be reopened — returning it to in-review status.</p>
			<p>Describe why the survey is being reopened. This will be recorded in the
				survey's history and may be visible to the supplier.</p>
			<FormControl
				as="textarea"
				rows={3}
				value={reopenNotes}
				onChange={e => setReopenNotes(e.target.value)}
				placeholder="Describe why this survey is being reopened..."
			/>
		</ConfirmationDialog>
		<CopyAnswersModal
			show={showCopyAnswersModal}
			targetConservationActionId={conservationAction?.conservationActionId}
			targetConservationActionTypeId={conservationAction?.conservationActionTypeId}
			targetSurveyTypeId={surveyTypeId}
			onHide={() => {
				setShowCopyAnswersModal(false);
			}}
			onConfirm={copyAnswers}
		/>
		<Col xs={12} className="sb-print-show">
			<h2 className="atomic-sectionheader">
				<span
					className="sb-survey-header sb-print-show">{conservationAction?.ContextNodeDetails?.name}{surveyType ? " - " + surveyType.name + " Survey" : ""}</span>
			</h2>
		</Col>
		<Col xs={12} className="sb-sticky-header">
			<h2 className="atomic-sectionheader">
				<div className="float-right btn-edit">
					<Conditional Condition={editMode}>
						<Button
							variant="save"
							disabled={!isValid}
							onClick={() => void saveAndExit()}
						>
							Save
						</Button>
						<Button
							variant="cancel"
							onClick={() => discardAndExit()}
						>
							Cancel
						</Button>
					</Conditional>
					<Conditional Condition={!editMode && canEdit()}>
						<Conditional Condition={canPullPreviousAnswers()}>
							<Button
								variant="link"
								onClick={x => {
									setShowCopyAnswersModal(true);
								}}>
								<Glyphicon glyph="copy"/>Copy Data From Existing Survey...
							</Button>
						</Conditional>
						<Button
							variant="link"
							onClick={() => onEditClick()}>
							<Glyphicon glyph="pencil"/>Edit
						</Button>
					</Conditional>
				</div>
				<span
					className="sb-survey-header">{conservationActionType?.name ?? ""}{surveyType ? ": " + surveyType.name : ""}</span>
				<InfoIcon link={survey.infoLink}>{survey.infoTitle}</InfoIcon>
			</h2>
		</Col>
		<Col xs={12}>
			<SurveySubmissionWidget
				conservationAction={conservationAction}
				surveyTypeId={surveyTypeId}
				survey={survey}
				answers={surveyAnswers}
				savedAnswers={savedSurveyAnswers}
				editMode={editMode}
				canReviewSubmissions={canReviewSubmissions}
				onEnterEditMode={canEdit() ? enterEditMode : null}
				onReloadData={reloadData}
				onSubmissionChange={setCurrentSubmission}
				submissionRefreshKey={submissionRefreshKey}
				isSectionComplete={isSectionComplete}
			/>
		</Col>
		<Col xs={12}>
			<SurveyForm
				editMode={editMode}
				
				conservationAction={conservationAction}
				survey={survey}
				answers={surveyAnswers}
				friendlyAnswers={friendlyAnswers}
				
				isValid={isValid}
				validation={validationObject}
				
				customSections={{
					"customRiskAssessment": RiskAssessments,
					"customPlantQuantity": PlantQuantities,
					"customModeledUplift": ModeledUpliftSurveySection,
					"customModeledUpliftReadOnly": ModeledUpliftReadOnlySurveySection,
					"customSubmissionBoundary": CustomSubmissionBoundary,
				}}
				isSectionReadOnly={isSectionReadOnly}
				customData={{
					PlantQuantities: surveyPlantQuantities,
					RiskAssessments: surveyRiskAssessments
				}}
				
				/* handlers */
				OnAnswerChange={onAnswerChange}
				OnValidation={onValidation}
				DeleteAnswer={deleteAnswer}
				OnConservationActionChange={OnConservationActionChange}
				OnCustomDataChange={onCustomDataChange}
				OnSave={() => void saveAndContinue()}
				OnFinish={() => void saveAndExit()}
			/>
		</Col>
	</Row>;
}


